import omit from 'lodash/omit';
import { storableError } from '../../util/errors';
import * as log from '../../util/log';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';

// ================ Action types ================ //

export const SET_SIGNUP_FROM_REQUEST = 'app/SignUpProjectPage/SET_SIGNUP_FROM_REQUEST';
export const SET_SIGNUP_FROM_SUCCESS = 'app/SignUpProjectPage/SET_SIGNUP_FROM_SUCCESS';
export const SET_SIGNUP_FROM_ERROR = 'app/SignUpProjectPage/SET_SIGNUP_FROM_ERROR';

// Create listing draft
export const CREATE_LISTING_DRAFT_REQUEST = 'app/SignUpProjectPage/CREATE_LISTING_DRAFT_REQUEST';
export const CREATE_LISTING_DRAFT_SUCCESS = 'app/SignUpProjectPage/CREATE_LISTING_DRAFT_SUCCESS';
export const CREATE_LISTING_DRAFT_ERROR = 'app/SignUpProjectPage/CREATE_LISTING_DRAFT_ERROR';

// Publish listing
export const PUBLISH_LISTING_REQUEST = 'app/SignUpProjectPage/PUBLISH_LISTING_REQUEST';
export const PUBLISH_LISTING_SUCCESS = 'app/SignUpProjectPage/PUBLISH_LISTING_SUCCESS';
export const PUBLISH_LISTING_ERROR = 'app/SignUpProjectPage/PUBLISH_LISTING_ERROR';

// Update listing
export const UPDATE_LISTING_REQUEST = 'app/SignUpProjectPage/UPDATE_LISTING_REQUEST';
export const UPDATE_LISTING_SUCCESS = 'app/SignUpProjectPage/UPDATE_LISTING_SUCCESS';
export const UPDATE_LISTING_ERROR = 'app/SignUpProjectPage/UPDATE_LISTING_ERROR';

// Image upload
export const UPLOAD_IMAGE_REQUEST = 'app/SignUpProjectPage/UPLOAD_IMAGE_REQUEST';
export const UPLOAD_IMAGE_SUCCESS = 'app/SignUpProjectPage/UPLOAD_IMAGE_SUCCESS';
export const UPLOAD_IMAGE_ERROR = 'app/SignUpProjectPage/UPLOAD_IMAGE_ERROR';

// Update image order
export const UPDATE_IMAGE_ORDER = 'app/SignUpProjectPage/UPDATE_IMAGE_ORDER';

// Remove image
export const REMOVE_IMAGE = 'app/SignUpProjectPage/REMOVE_IMAGE';

// Clear updated tab
export const CLEAR_UPDATED_TAB = 'app/SignUpProjectPage/CLEAR_UPDATED_TAB';

// ================ Reducer ================ //

const initialState = {
  signUpFrom: null,
  createListingDraftInProgress: false,
  createListingDraftError: null,
  publishListingInProgress: false,
  publishListingError: null,
  updateListingInProgress: false,
  updateListingError: null,
  uploadImageInProgress: false,
  uploadImageError: null,
  images: {},
  imageOrder: [],
  removedImageIds: [],
  submittedListingId: null,
  redirectToListing: false,
  updatedTab: null,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case SET_SIGNUP_FROM_REQUEST:
      return {
        ...state,
        signUpFrom: payload,
      };
    case SET_SIGNUP_FROM_SUCCESS:
      return {
        ...state,
        signUpFrom: payload,
      };
    case SET_SIGNUP_FROM_ERROR:
      return {
        ...state,
        signUpFrom: payload,
      };

    case CREATE_LISTING_DRAFT_REQUEST:
      return {
        ...state,
        createListingDraftInProgress: true,
        createListingDraftError: null,
        submittedListingId: null,
        redirectToListing: false,
      };
    case CREATE_LISTING_DRAFT_SUCCESS:
      return {
        ...state,
        createListingDraftInProgress: false,
        submittedListingId: payload.data.id,
      };
    case CREATE_LISTING_DRAFT_ERROR:
      return {
        ...state,
        createListingDraftInProgress: false,
        createListingDraftError: payload,
      };

    case PUBLISH_LISTING_REQUEST:
      return {
        ...state,
        publishListingInProgress: true,
        publishListingError: null,
        redirectToListing: false,
      };
    case PUBLISH_LISTING_SUCCESS:
      return {
        ...state,
        publishListingInProgress: false,
        redirectToListing: true,
      };
    case PUBLISH_LISTING_ERROR:
      return {
        ...state,
        publishListingInProgress: false,
        publishListingError: payload,
      };

    case UPDATE_LISTING_REQUEST:
      return {
        ...state,
        updateListingInProgress: true,
        updateListingError: null,
        updatedTab: payload.params.tab,
      };
    case UPDATE_LISTING_SUCCESS:
      return {
        ...state,
        updateListingInProgress: false,
      };
    case UPDATE_LISTING_ERROR:
      return {
        ...state,
        updateListingInProgress: false,
        updateListingError: payload,
      };

    case UPLOAD_IMAGE_REQUEST:
      return {
        ...state,
        uploadImageInProgress: true,
        uploadImageError: null,
      };
    case UPLOAD_IMAGE_SUCCESS: {
      const { imageId, imageData } = payload;
      const imageOrder = state.imageOrder.concat([imageId]);
      return {
        ...state,
        uploadImageInProgress: false,
        images: { ...state.images, [imageId]: imageData },
        imageOrder,
      };
    }
    case UPLOAD_IMAGE_ERROR:
      return {
        ...state,
        uploadImageInProgress: false,
        uploadImageError: payload,
      };

    case UPDATE_IMAGE_ORDER:
      return {
        ...state,
        imageOrder: payload.imageOrder,
      };

    case REMOVE_IMAGE: {
      const imageIdToRemove = payload.imageId;
      const images = omit(state.images, imageIdToRemove);
      const imageOrder = state.imageOrder.filter(id => id !== imageIdToRemove);
      const removedImageIds = state.removedImageIds.concat([imageIdToRemove]);
      return {
        ...state,
        images,
        imageOrder,
        removedImageIds,
      };
    }

    case CLEAR_UPDATED_TAB:
      return {
        ...state,
        updatedTab: null,
      };

    default:
      return state;
  }
}

// ================ Action creators ================ //

export const setSignUpFromRequest = () => ({
  type: SET_SIGNUP_FROM_REQUEST,
});

export const setSignUpFromSuccess = from => ({
  type: SET_SIGNUP_FROM_SUCCESS,
  payload: from,
});

export const setSignUpFromError = error => ({
  type: SET_SIGNUP_FROM_ERROR,
  payload: error,
});

const requestAction = actionType => params => ({ type: actionType, payload: { params } });
const successAction = actionType => result => ({ type: actionType, payload: result.data });
const errorAction = actionType => error => ({ type: actionType, payload: error, error: true });

// Create listing draft
export const createListingDraftRequest = requestAction(CREATE_LISTING_DRAFT_REQUEST);
export const createListingDraftSuccess = successAction(CREATE_LISTING_DRAFT_SUCCESS);
export const createListingDraftError = errorAction(CREATE_LISTING_DRAFT_ERROR);

// Publish listing
export const publishListingRequest = requestAction(PUBLISH_LISTING_REQUEST);
export const publishListingSuccess = successAction(PUBLISH_LISTING_SUCCESS);
export const publishListingError = errorAction(PUBLISH_LISTING_ERROR);

// Update listing
export const updateListingRequest = requestAction(UPDATE_LISTING_REQUEST);
export const updateListingSuccess = successAction(UPDATE_LISTING_SUCCESS);
export const updateListingError = errorAction(UPDATE_LISTING_ERROR);

// Image upload
export const uploadImageRequest = requestAction(UPLOAD_IMAGE_REQUEST);
export const uploadImageSuccess = (imageId, imageData) => ({
  type: UPLOAD_IMAGE_SUCCESS,
  payload: { imageId, imageData },
});
export const uploadImageError = errorAction(UPLOAD_IMAGE_ERROR);

// Update image order
export const updateImageOrder = imageOrder => ({
  type: UPDATE_IMAGE_ORDER,
  payload: { imageOrder },
});

// Remove image
export const removeListingImage = imageId => ({
  type: REMOVE_IMAGE,
  payload: { imageId },
});

// Clear updated tab
export const clearUpdatedTab = () => ({
  type: CLEAR_UPDATED_TAB,
});

// ================ Thunks ================ //

export const requestCreateListingDraft = values => (dispatch, getState, sdk) => {
  dispatch(createListingDraftRequest(values));

  // Match the structure expected by the original EditProjectListingPage
  const data = {
    ...values,
    publicData: {
      ...(values.publicData || {}),
      listingType: 'project',
    },
  };

  return sdk.ownListings
    .createDraft(data, {
      expand: true,
      include: ['author', 'images'],
      'fields.listing': [
        'title',
        'description',
        'geolocation',
        'publicData',
        'metadata',
        'state',
        'createdAt',
        'price',
      ],
      'fields.user': ['profile.displayName', 'profile.abbreviatedName'],
      'fields.image': ['variants.landscape-crop', 'variants.landscape-crop2x'],
    })
    .then(response => {
      dispatch(addMarketplaceEntities(response));
      dispatch(createListingDraftSuccess(response));
      return response;
    })
    .catch(e => {
      log.error(e, 'create-listing-draft-failed');
      dispatch(createListingDraftError(storableError(e)));
    });
};

export const requestPublishListingDraft = (listingId, currentUser) => (dispatch, getState, sdk) => {
  dispatch(publishListingRequest(listingId));

  return sdk.ownListings
    .publishDraft({ id: listingId }, { expand: true, include: ['author'] })
    .then(response => {
      // Add the updated listing to the Redux store
      dispatch(addMarketplaceEntities(response));
      dispatch(publishListingSuccess(response));
      return response;
    })
    .catch(e => {
      log.error(e, 'publish-listing-draft-failed');
      dispatch(publishListingError(storableError(e)));
    });
};

export const requestUpdateListing = (tab, values) => (dispatch, getState, sdk) => {
  dispatch(updateListingRequest({ tab }));

  const { id, ...rest } = values;
  const bodyParams = {
    id,
    ...rest,
  };

  return sdk.ownListings
    .update(bodyParams, {
      expand: true,
      include: ['author', 'images'],
    })
    .then(response => {
      dispatch(addMarketplaceEntities(response));
      dispatch(updateListingSuccess(response));
      return response;
    })
    .catch(e => {
      log.error(e, 'update-listing-failed');
      dispatch(updateListingError(storableError(e)));
    });
};

export const requestImageUpload = data => (dispatch, getState, sdk) => {
  dispatch(uploadImageRequest(data));

  return sdk.images
    .upload(data, {
      expand: true,
    })
    .then(resp => {
      const imageId = resp.data.data.id;
      const imageData = resp.data.data;
      dispatch(uploadImageSuccess(imageId, imageData));
      return resp;
    })
    .catch(e => {
      dispatch(uploadImageError(storableError(e)));
      throw e;
    });
};

// ================ Thunks ================ //

export const setSignUpFrom = from => dispatch => {
  dispatch(setSignUpFromRequest());
  dispatch(setSignUpFromSuccess(from));
  return from;
};
